import { ReactNode, useEffect, useState } from "react";
import {
  useCreateHubspotUrlMutation,
  useCreateSalesforceUrlMutation,
} from "../../api";
import { toast } from "react-toastify";
import { CustomToast } from "../ui/CustomToast";
import { getErrorMessage } from "../../utils/getErrorMessage";
import { useNavigate } from "react-router-dom";
import { AppRoutes } from "../../types/enums";
import { useAppDispatch } from "../../hooks";
import { setIntegrationData } from "../../features/companySlice";
import { CustomLoader } from "../ui/CustomLoader";

export const Integration = ({ children }: { children: ReactNode }) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const urlParams = new URLSearchParams(window.location.search);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const [createHubspotUrl] = useCreateHubspotUrlMutation();
  const [createSalesforceUrl] = useCreateSalesforceUrlMutation();

  const startStepHandler = (
    type: string,
    path: string,
    actionType: "create" | "update" | "add-contacts" | "connect",
    campaignId: string,
  ) => {
    dispatch(
      setIntegrationData({
        campaignId: campaignId,
        campaignImportFlow: type,
        actionType: actionType,
      }),
    );

    navigate(
      actionType === "create"
        ? `${AppRoutes.CREATE_CAMPAIGN}?crm=${type}`
        : `${path}?crm=${type}&actionType=${actionType}`,
      {
        replace: true,
      },
    );
  };

  const integrationHandler = async () => {
    try {
      const type = urlParams.get("type");
      const code = urlParams.get("code");
      const state = urlParams.get("state");

      const data = JSON.parse(atob(state || "")) || "";

      if (data.token && code) {
        navigate(
          `${AppRoutes.CRM}/${data.campaign_id}?code=${code}&state=${state}&platform=${type}`,
          { replace: true },
        );
        return;
      }

      if (type === "hubspot" && code && data) {
        await createHubspotUrl({ code })
          .unwrap()
          .then(() => {
            startStepHandler(
              type,
              data.redirect_path,
              data.action_type,
              data.campaign_id,
            );
          })
          .catch((err) => {
            toast.error(
              <CustomToast
                type="error"
                text={
                  getErrorMessage(err).globalError ||
                  "Something went wrong during connecting to HubSpot. Please try again"
                }
                title="Connection to HubSpot failed."
              />,
            );
          });
      } else if (type === "salesforce" && code) {
        await createSalesforceUrl({ code })
          .unwrap()
          .then(() => {
            startStepHandler(
              type,
              data.redirect_path,
              data.action_type,
              data.campaign_id,
            );
          })
          .catch((err) => {
            toast.error(
              <CustomToast
                type="error"
                text={
                  getErrorMessage(err).globalError ||
                  "Something went wrong during connecting to Salesforce. Please try again"
                }
                title="Connection to Salesforce failed."
              />,
            );
          });
      }
    } catch (e) {
      console.error("[Integration] parse error:", e);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    void integrationHandler();
  }, []);

  if (isLoading) return <CustomLoader />;
  return <>{children}</>;
};
